import {
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type MouseEvent as ReactMouseEvent,
} from 'react';
import { Check, ChevronDown, Search, X } from 'lucide-react';
import { Tooltip } from '@parrot/ui';
import { cx } from '@/lib/cx';
import { groupPortfolios, type Portfolio } from '@/lib/portfolio';
import styles from './PortfolioPicker.module.css';

export type PortfolioPickerProps = {
  /** Picker list (any order — grouped My → Team here). */
  portfolios: Portfolio[];
  /** Selected portfolio id (undefined → none). */
  value?: string;
  onChange: (id: string) => void;
  /**
   * Clears the current selection (returns the picker to "None"). Rendered as a
   * red ✕ that replaces the chevron on hover/focus of the trigger — but ONLY
   * when a portfolio is selected (there is nothing to clear otherwise).
   */
  onClear?: () => void;
  /** Signed-in user's email — splits D360 portfolios into "My" / "Team". */
  currentUserEmail?: string;
  /** Lists still loading — trigger shows "Loading…" and is disabled. */
  loading?: boolean;
  /**
   * The list fetch FAILED (vs. simply empty). Surfaces a distinct "Unavailable"
   * trigger label so an errored fetch is not indistinguishable from an empty
   * list. Best-effort — purely cosmetic, does not change behaviour otherwise.
   */
  error?: boolean;
  className?: string;
};

type Section = { key: string; label: string; items: Portfolio[] };

/**
 * Compact SINGLE-select portfolio dropdown ("Portfolio: My Netflix Slate ▾").
 *
 * WAI-ARIA combobox-with-listbox: the trigger opens a popover with a search box
 * (role=combobox, aria-activedescendant) driving a grouped role=listbox
 * (role=group per My / Team section, role=option rows).
 * Keyboard: ↓/↑ move, Home/End jump, Enter selects, Esc / Tab close (Esc returns
 * focus to the trigger). Click outside closes.
 *
 * Ported verbatim from licensing-intelligence, minus the "Client portfolios"
 * group (streaming has no licensing client portfolios).
 */
export function PortfolioPicker({
  portfolios,
  value,
  onChange,
  onClear,
  currentUserEmail,
  loading = false,
  error = false,
  className,
}: PortfolioPickerProps) {
  const uid = useId();
  const listId = `${uid}-list`;
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [active, setActive] = useState(0);
  const wrapRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selected = portfolios.find((p) => p.id === value);

  /**
   * Trigger VALUE text (the part after "Portfolio:"). It tells the user WHY the
   * control is inert instead of showing a bare greyed-out "None":
   *   loading                         -> "Loading…"
   *   has selection                   -> the portfolio name
   *   has list, none chosen           -> "None"   (happy path always wins)
   *   empty list + fetch errored      -> "Unavailable"
   *   empty list, no error            -> "No portfolios"
   */
  const triggerValue = loading
    ? 'Loading…'
    : selected
      ? selected.name
      : portfolios.length > 0
        ? 'None'
        : error
          ? 'Unavailable'
          : 'No portfolios';

  const sections = useMemo<Section[]>(() => {
    const q = query.trim().toLowerCase();
    const match = (p: Portfolio) => !q || p.name.toLowerCase().includes(q);
    const { mine, team } = groupPortfolios(portfolios, currentUserEmail);
    return [
      { key: 'mine', label: 'My portfolios', items: mine.filter(match) },
      { key: 'team', label: 'Team portfolios', items: team.filter(match) },
    ].filter((s) => s.items.length > 0);
  }, [portfolios, currentUserEmail, query]);
  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections]);

  function openMenu() {
    setQuery('');
    setOpen(true);
  }
  function close(returnFocus: boolean) {
    setOpen(false);
    if (returnFocus) triggerRef.current?.focus();
  }
  function choose(id: string) {
    onChange(id);
    close(true);
  }

  // On open: focus the search box and highlight the current selection.
  useEffect(() => {
    if (!open) return;
    searchRef.current?.focus();
    const idx = flat.findIndex((p) => p.id === value);
    setActive(idx >= 0 ? idx : 0);
    // Only on open — typing resets `active` below.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Click outside closes.
  useEffect(() => {
    if (!open) return undefined;
    function onDown(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  // Keep the highlighted option in view.
  useEffect(() => {
    if (!open) return;
    document.getElementById(`${uid}-opt-${active}`)?.scrollIntoView({ block: 'nearest' });
  }, [active, open, uid]);

  function onSearchKey(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActive((i) => Math.min(i + 1, flat.length - 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActive((i) => Math.max(i - 1, 0));
    } else if (e.key === 'Home') {
      e.preventDefault();
      setActive(0);
    } else if (e.key === 'End') {
      e.preventDefault();
      setActive(Math.max(flat.length - 1, 0));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      const p = flat[active];
      if (p) choose(p.id);
    } else if (e.key === 'Escape') {
      e.preventDefault();
      close(true);
    } else if (e.key === 'Tab') {
      close(false);
    }
  }

  function onTriggerKey(e: KeyboardEvent<HTMLButtonElement>) {
    if (!open && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
      e.preventDefault();
      openMenu();
    }
  }

  let optIndex = -1;
  // The ✕/clear affordance exists only when there's something to clear. When it
  // exists it is always mounted, and CSS reveals it (swapping it in for the
  // chevron) on hover/keyboard-focus of the control — see `.wrap:hover` /
  // `:focus-within` in the stylesheet. Keeping it mounted (rather than toggling
  // on a JS hover flag) means a pointer press never races a state change that
  // could unmount it mid-click.
  const canClear = Boolean(selected) && Boolean(onClear);

  function handleClear(e: ReactMouseEvent<HTMLButtonElement>) {
    // Must NOT open the dropdown. The ✕ is a sibling of the trigger (not nested),
    // so this no longer bubbles to the trigger's onClick — stop it anyway so no
    // future layout change can accidentally re-open the menu.
    e.stopPropagation();
    onClear?.();
  }

  return (
    <div ref={wrapRef} className={cx(styles.wrap, canClear && styles.clearable, className)}>
      <button
        ref={triggerRef}
        type={'button'}
        className={cx(styles.trigger, open && styles.triggerOpen)}
        aria-haspopup={'listbox'}
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        aria-label={`Portfolio: ${selected?.name ?? triggerValue}`}
        disabled={loading || portfolios.length === 0}
        onClick={() => (open ? close(false) : openMenu())}
        onKeyDown={onTriggerKey}
      >
        <span className={styles.triggerLabel}>{'Portfolio:'}</span>
        <span className={styles.triggerValue}>{triggerValue}</span>
        {/* Chevron. Hidden by CSS (not unmounted) while the clear ✕ is revealed,
            so the trigger keeps its layout. */}
        <ChevronDown
          size={14}
          className={cx(styles.chevron, open && styles.chevronOpen)}
          aria-hidden={'true'}
        />
      </button>

      {/* Clear (✕): a sibling button overlaid where the chevron sits (never
          nested inside the trigger <button>). Mounted whenever there is a
          selection to clear; CSS reveals it on hover/focus-within and hides it
          otherwise. Clicking it clears the selection and must NOT open the
          dropdown. */}
      {canClear && onClear && (
        <Tooltip title={'Clear Selection'} content={'Clear Selection'} placement={'top'}>
          <button
            type={'button'}
            aria-label={'Clear selection'}
            className={styles.clear}
            onClick={handleClear}
            onMouseDown={(e) => e.preventDefault()}
          >
            <X size={14} aria-hidden={'true'} />
          </button>
        </Tooltip>
      )}

      {open && (
        <div className={styles.menu}>
          <div className={styles.search}>
            <Search size={14} className={styles.searchIcon} aria-hidden={'true'} />
            <input
              ref={searchRef}
              className={styles.searchInput}
              role={'combobox'}
              aria-label={'Search portfolios'}
              aria-expanded={true}
              aria-controls={listId}
              aria-autocomplete={'list'}
              aria-activedescendant={flat[active] ? `${uid}-opt-${active}` : undefined}
              placeholder={'Search portfolios'}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setActive(0);
              }}
              onKeyDown={onSearchKey}
            />
          </div>
          <div id={listId} role={'listbox'} aria-label={'Portfolios'} className={styles.list}>
            {sections.length === 0 && (
              <div className={styles.empty}>{'No matching portfolios'}</div>
            )}
            {sections.map((s) => (
              <div
                key={s.key}
                role={'group'}
                aria-labelledby={`${uid}-${s.key}`}
                className={styles.group}
              >
                <div id={`${uid}-${s.key}`} role={'presentation'} className={styles.groupLabel}>
                  {s.label}
                </div>
                {s.items.map((p) => {
                  optIndex += 1;
                  const i = optIndex;
                  const isSel = p.id === value;
                  return (
                    <div
                      key={p.id}
                      id={`${uid}-opt-${i}`}
                      role={'option'}
                      aria-selected={isSel}
                      className={cx(
                        styles.option,
                        i === active && styles.optionActive,
                        isSel && styles.optionSelected,
                      )}
                      onMouseEnter={() => setActive(i)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => choose(p.id)}
                    >
                      <span className={styles.optionName}>{p.name}</span>
                      {isSel && <Check size={14} className={styles.check} aria-hidden={'true'} />}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
